/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */
/**
 * Root reducers
 *
 * @author Mikhail Kokoulin
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Julia Ignatieva
 * @author Dmitriy Kvon
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.32.0
 * @since 1.0.0
 */

import { connectRouter, LocationChangeAction, RouterState } from 'connected-react-router';
import { combineReducers, Reducer } from 'redux';

import { IWizardAdditionalReportsReducer } from '@pages/WizardAdditionalReports/wizardAdditionalReportsReducer';
import {
    IWizardChangeRoleReducer,
} from '@pages/WizardChangeRole/wizardChangeRoleReducer';

import { pageApi } from '../api/page.api';
import headerReducer, { IHeaderReducer } from '../components/Header/headerReducer';
import basketReducer, { IBasketState } from '../components/Table/components/footerModal/Basket/basketReducer';
import createInquiryReducer from '../components/Table/components/footerModal/CreateInquiry/createInquiryReducer';
import { ICreateInquiryReducer } from '../components/Table/components/footerModal/CreateInquiry/types/createInquiryTypes';
import labelEditorReducer, {
    ILabelEditorReducerState,
} from '../components/Table/components/footerModal/LabelEditor/labelEditorReducer';
import tableReducer, { ITableReducer } from '../components/Table/reducers/tableReducer';
import appHistory from '../modules/app/appHistory';
import appReducer from '../modules/app/appReducer';
import { IAppReducerState } from '../modules/app/appTypes';
import {
    asyncStateManagementReducer,
    IAsyncStateManagementReducer,
} from '../modules/asyncStateManagment/asyncStateManagementReducer';
import bannerReducer, { IBannerReducer } from '../modules/banner/bannerReducers';
import breadCrumbsReducer, { IBreadCrumbsState } from '../modules/breadCrumbs/breadCrumbsReducer';
import captchaReducer, { ICaptchaState } from '../modules/captcha/captchaReducer';
import exportReducer from '../modules/exports/exportReducer';
import { IExportState } from '../modules/exports/exportTypes';
import { IFormAddressReducer } from '../modules/formAddress/formAddressReducer';
import { inquiryApi } from '../modules/inquiry/inquiryApi';
import intReducer, { IIntState } from '../modules/internationalization/intReducer';
import loaderReducer, { ILoaderReducer } from '../modules/loader/loaderReducer';
import loggingReducer, { ILoggingReducer } from '../modules/logging/loggingReducer';
import modalReducer, { IModalReducer } from '../modules/modal/modalReducer';
import notificationReducer, { INotificationState } from '../modules/notification/notificationReducer';
import onboardingReducer, { IOnboardingReducer } from '../modules/onboarding/onboardingReducer';
import { IPersonalDataDeleteReducer } from '../modules/personalDataDelete/personalDataDeleteReducer';
import pollingReducer from '../modules/polling/pollingReducer';
import { IPollingState } from '../modules/polling/pollingTypes';
import promisedPaymentReducer, { IPromisedPaymentState } from '../modules/promisedPayment/promisedPaymentReducer';
import guardReducer, { IGuardReducer } from '../modules/role/reducer/guardReducer';
import storageReducer, { IStorageReducerState } from '../modules/storage/storageReducer';
import superSearchReducer, { ISuperSearchReducerState } from '../modules/superSearch/superSearchReducer';
import { ITableSlice, tableSlice } from '../modules/table/slice';
import { tableApi } from '../modules/table/store';
import { notificationsSlice } from '../modules/userNotifications/slice';
import { INotificationsSlice } from '../modules/userNotifications/types';
import wizardReducer, { IWizardState } from '../modules/wizard/wizardReducer';
import moneyTransferReducer, { IMoneyTransferReducer } from '../pages/MoneyTransfer/moneyTransferReducer';
import { IRootAuthReducer, rootAuthReducer } from '../pages/PageAuth/reducers';
import { IAutoReportIdReducer } from '../pages/PageAutoReportId/reducer';
import { ICampaignInfoReducer } from '../pages/PageCampaignInfo/campaignInfoReducer';
import { IPageCashbackReducer } from '../pages/PageCashback/pageCashbackReducer';
import dashboardReducer, { IDashboardReducer } from '../pages/PageDashboard/reducers/dashboardReducer';
import expensesAnalyzerPageReducer, {
    IPageExpensesAnalyzerReducer,
} from '../pages/PageExpensesAnalyzer/pageExpensesAnalyzerReducer';
import { ILabelsImportReducer, labelsImportReducer } from '../pages/PageLabelsImport/labelsImportReducer';
import { IProductsState, tariffsAndServicesReducer } from '../pages/PageProducts/tariffsAndServicesReducer';
import { constructorSlice } from '../pages/PageReportsConstructor/slice';
import settingsReducer, { ISettingsReducer } from '../pages/PageSettings/settingsReducer';
import {
    extFilterReducer,
    IExtFilterReducer,
} from '../pages/PageSubscriberMobile/components/SubscriberMobileExtFilter/reducers';
import { IWizardAdditionalAccountsReducer } from '../pages/WizardAdditionalAccounts/wizardAdditionalAccountsReducer';
import { IWizardBillsDocReducer } from '../pages/WizardBillsDoc/wizardBillsDocReducer';
import { callsDetailReducer, ICallsDetailState } from '../pages/WizardCallsDetail/callsDetailReducer';
import { IWizardChangeLockReducer } from '../pages/WizardChangeLock/wizardChangeLockReducer';
import { IWizardCollationReportReducer } from '../pages/WizardCollationReport/wizardCollationReportReducer';
import { IWizardExpensesDetailReducer } from '../pages/WizardExpensesDetail/wizardExpensesDetailReducer';
import { IWizardImportPersonalDataReducer } from '../pages/WizardImportPersonalData/wizardImportPersonalDataReducer';
import { IWizardImportSubscribersLimitsReducer } from '../pages/WizardImportSubscribersLimits/wizardImportSubscribersLimitsReducer';
import { IWizardMultiSearchReducer } from '../pages/WizardMultiSearch/reducer';
import {
    IWizardProductOfferingsState,
    wizardProductOfferingsReducer,
} from '../pages/WizardProductOfferings/wizardProductOfferingsReducer';
import { IWizardOrderReportReducer } from '../pages/WizardReportsConstructor/WizardOrderReport/reducer';
import { IAsyncStore, TStore } from '../types/globalTypes';
import { IReportsConstructorSlice } from './../pages/PageReportsConstructor/types';
import { IWizardOrderTemplateReducer } from './../pages/WizardReportsConstructor/WizardOrderTemplate/reducer';
import globalReducer, { IGlobalReducer } from './globalReducer';

export interface IAsyncReducer {
    autoReportId: IAutoReportIdReducer;
    campaignInfo: ICampaignInfoReducer;
    cashback: IPageCashbackReducer;
    formAddress: IFormAddressReducer;
    personalDataDelete: IPersonalDataDeleteReducer;
    wizardAdditionalAccounts: IWizardAdditionalAccountsReducer;
    wizardBillsDoc: IWizardBillsDocReducer;
    wizardChangeLock: IWizardChangeLockReducer;
    wizardExpensesDetail: IWizardExpensesDetailReducer;
    wizardImportPersonalData: IWizardImportPersonalDataReducer;
    wizardMultiSearch: IWizardMultiSearchReducer;
    wizardOrderTemplate: IWizardOrderTemplateReducer;
    wizardOrderReport: IWizardOrderReportReducer;
    wizardAdditionalReports: IWizardAdditionalReportsReducer;
    wizardCollationReport: IWizardCollationReportReducer;
    wizardImportSubscribersLimits: IWizardImportSubscribersLimitsReducer;
    wizardChangeRole: IWizardChangeRoleReducer;
}

export interface IRootReducer extends Partial<IAsyncReducer> {
    app: IAppReducerState;
    asyncStateManagement: IAsyncStateManagementReducer;
    auth: IRootAuthReducer;
    banner: IBannerReducer;
    basket: IBasketState;
    breadCrumbs: IBreadCrumbsState;
    callsDetail: ICallsDetailState;
    captcha: ICaptchaState;
    createInquiry: ICreateInquiryReducer;
    dashboard: IDashboardReducer;
    expensesAnalyzer: IPageExpensesAnalyzerReducer;
    exports: IExportState;
    global: IGlobalReducer;
    guard: IGuardReducer;
    header: IHeaderReducer;
    internationalization: IIntState;
    labelEditor: ILabelEditorReducerState;
    labelsImport: ILabelsImportReducer;
    loader: ILoaderReducer;
    logging: ILoggingReducer;
    modal: IModalReducer;
    moneyTransfer: IMoneyTransferReducer;
    notification: INotificationState;
    [notificationsSlice.name]: INotificationsSlice;
    onboarding: IOnboardingReducer;
    polling: IPollingState;
    products: IProductsState;
    promisedPayment: IPromisedPaymentState;
    router: Reducer<RouterState, LocationChangeAction>;
    [constructorSlice.name]: IReportsConstructorSlice;
    settings: ISettingsReducer;
    storage: IStorageReducerState;
    subscriberMobileExtFilter: IExtFilterReducer;
    superSearch: ISuperSearchReducerState;
    tableReducer: ITableReducer;
    [tableSlice.name]: ITableSlice;
    wizard: IWizardState;
    wizardProductOfferings: IWizardProductOfferingsState;
}

export const createReducer = (asyncReducers?: IAsyncStore['asyncReducers']) =>
    combineReducers({
        app: appReducer,
        auth: rootAuthReducer(),
        asyncStateManagement: asyncStateManagementReducer,
        banner: bannerReducer,
        basket: basketReducer,
        breadCrumbs: breadCrumbsReducer,
        callsDetail: callsDetailReducer,
        captcha: captchaReducer,
        createInquiry: createInquiryReducer,
        dashboard: dashboardReducer(),
        expensesAnalyzer: expensesAnalyzerPageReducer,
        exports: exportReducer,
        global: globalReducer,
        guard: guardReducer,
        header: headerReducer,
        internationalization: intReducer,
        labelEditor: labelEditorReducer,
        labelsImport: labelsImportReducer,
        loader: loaderReducer,
        logging: loggingReducer,
        modal: modalReducer,
        moneyTransfer: moneyTransferReducer,
        notification: notificationReducer,
        onboarding: onboardingReducer,
        polling: pollingReducer,
        products: tariffsAndServicesReducer,
        promisedPayment: promisedPaymentReducer,
        router: connectRouter(appHistory),
        settings: settingsReducer(),
        storage: storageReducer,
        subscriberMobileExtFilter: extFilterReducer(),
        superSearch: superSearchReducer,
        tableReducer: tableReducer,
        wizard: wizardReducer,
        wizardProductOfferings: wizardProductOfferingsReducer,
        [pageApi.reducerPath]: pageApi.reducer,
        [inquiryApi.reducerPath]: inquiryApi.reducer,
        [tableApi.reducerPath]: tableApi.reducer,
        [tableSlice.name]: tableSlice.reducer,
        [constructorSlice.name]: constructorSlice.reducer,
        [notificationsSlice.name]: notificationsSlice.reducer,
        ...asyncReducers,
    });

export function injectAsyncReducer(store: TStore, name: string, asyncReducer: Reducer) {
    store.injectReducer?.(name, asyncReducer);
}
